import { Box, ButtonBase, LinearProgress, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { Icon } from '@iconify/react';
import { JSX, useEffect } from 'react';
import { makeSignedS3Url } from '../../../common/helpers/makeSignedS3Url';
import { ReportFile, ReportSection } from '../../../common/types/reports';
import { useDropzone } from 'react-dropzone';
import { UseReportUpdaterResult } from '../../hooks/useReportUpdater';
import { useUploadReportFile } from '../../../common/components/mutations';
import DataGrid from '../../../common/components/dataGrid';
import DataGridDeleteAction from '../../../common/components/dataGrid/dataGridDeleteAction';

interface ReportSectionFilesProps
  extends Pick<UseReportUpdaterResult, 'addFile' | 'removeFile' | 'setPropertyValue'> {
  /**
   * Report's section data to get the files from
   */
  sectionData: ReportSection;
  /**
   * Template file url to allow user to download it
   */
  templateFileUrl?: string;
}

/**
 * Component to manage report's section in manual mode (download template file, upload and manage section files)
 */
const ReportSectionFiles = ({
  sectionData,
  templateFileUrl,
  addFile,
  removeFile,
  setPropertyValue,
}: ReportSectionFilesProps): JSX.Element => {
  const { mutateAsync, isPending } = useUploadReportFile();
  const onDrop = ([file]: File[]): void => {
    if (!file) {
      return;
    }
    mutateAsync({ file, reportId: sectionData.reportID }).then((uploadedFiles) => {
      uploadedFiles.map(({ url, name }) => addFile(url, name));
    });
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    disabled: isPending,
    multiple: false,
    accept: {
      'application/pdf': ['.pdf'],
    },
  });

  // Mark section as manual (report builder uses this indicator to decide if PDF should be generated or not)
  useEffect(() => {
    setPropertyValue('sectionType', 'manual');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns: GridColDef<ReportFile>[] = [
    {
      field: 'fileUploadName',
      headerName: 'File name',
      flex: 1,
      renderCell: ({ row }) => (
        <a href={makeSignedS3Url(row.fileUrl)} target="_blank" rel="noreferrer">
          <Typography color="primary.main" className="hover:underline">
            {row.fileUploadName}
          </Typography>
        </a>
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      getActions: ({ row }) => [
        <DataGridDeleteAction key="delete" onClick={(): void => removeFile(row.id)} />,
      ],
    },
  ];

  return (
    <Box>
      <Box className="grid grid-cols-2 gap-8">
        {templateFileUrl && (
          <ButtonBase
            className="space-x-3 h-16 w-full"
            sx={{ color: 'text.secondary', border: 1, borderRadius: 1 }}
            component="a"
            href={templateFileUrl}
            target="_blank"
          >
            <Icon icon="icon-park-outline:file-quality-one" width={16} />
            <Typography fontWeight={500}>Use suggested template</Typography>
          </ButtonBase>
        )}
        <Box className="space-y-1">
          <ButtonBase
            {...getRootProps()}
            className="flex-col h-16 w-full"
            sx={{
              color: 'primary.main',
              border: 1,
              borderStyle: 'dashed',
              borderRadius: 1,
              borderColor: '#325B853D',
              bgcolor: '#F8FAFD',
            }}
          >
            <input {...getInputProps()} />
            <Box className="flex space-x-2">
              <Icon icon="icon-park-outline:upload-two" width={16} />
              <Typography fontWeight={500}>Upload a new file</Typography>
            </Box>
            {isPending && <LinearProgress className="mt-1 w-32" />}
          </ButtonBase>
          <Typography fontWeight={500}>
            Upload your documents
            <Typography variant="body2" color="text.secondary" className="inline pl-2">
              DOC, DOCX, PDF, CSV, XLSX, or XLS files accepted
            </Typography>
          </Typography>
        </Box>
      </Box>

      {sectionData.files.length > 0 && (
        <DataGrid
          enableQuickFilter={false}
          columns={columns}
          rows={sectionData.files}
          getRowId={(item): string => item.id.toString()}
        />
      )}
    </Box>
  );
};

export default ReportSectionFiles;
